import { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/auth/useAuth";
import { Home, LayoutDashboard, Menu, Trophy, X } from "lucide-react";

export default function AdminLayout() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { loading, isAdmin, firebaseUser } = useAuth();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;

    function onEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [open]);

  if (loading) {
    return <div className="p-6">Loading admin...</div>;
  }

  if (!firebaseUser) {
    return <Navigate to="/" replace />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-semibold">Admin Access Required</h1>
          <p className="text-sm text-muted">
            Your account does not have access to this section.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-background">
      <header className="sticky top-0 z-30 flex h-16 min-w-0 items-center justify-between border-b border-black/5 bg-surface/95 px-4 backdrop-blur-sm md:px-8">
        <div className="flex min-w-0 items-center gap-4">
          <button
            onClick={() => setOpen(true)}
            className="shrink-0 rounded-lg border border-black/10 p-2 text-text md:hidden"
            aria-label="Open admin menu"
          >
            <Menu size={18} aria-hidden="true" />
          </button>

          <div className="min-w-0">
            <div className="truncate text-sm font-bold tracking-wide text-text">
              KGA Tour Admin
            </div>
            <div className="truncate text-xs text-mutedText md:hidden">
              {getPageLabel(location.pathname)}
            </div>
          </div>
        </div>

        <div className="hidden shrink-0 rounded-full bg-black/[0.03] px-3 py-1 text-xs font-semibold text-mutedText md:block">
          {getPageLabel(location.pathname)}
        </div>
      </header>

      <div className="flex min-w-0 flex-1">
        <aside className="hidden w-64 shrink-0 flex-col gap-2 border-r border-black/5 bg-surface px-5 py-6 md:flex">
          <SidebarLink
            to="/admin"
            label="Dashboard"
            icon={<LayoutDashboard size={17} />}
          />
          <SidebarLink
            to="/admin/tours"
            label="Tours"
            icon={<Trophy size={17} />}
          />
          <div className="my-3 h-px bg-black/5" />
          <SidebarLink
            to="/more"
            label="Back to Site"
            icon={<Home size={17} />}
          />
        </aside>

        {open && (
          <>
            <div
              className="fixed inset-0 bg-black/30 z-40 md:hidden"
              onClick={() => setOpen(false)}
            />
            <aside className="fixed left-0 top-0 z-50 h-full w-[min(18rem,calc(100vw-2rem))] border-r border-black/5 bg-surface px-5 py-6 md:hidden">
              <div className="mb-6 flex items-center justify-between gap-3">
                <div className="font-bold">Admin</div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-lg border border-black/10 p-2"
                  aria-label="Close admin menu"
                >
                  <X size={16} aria-hidden="true" />
                </button>
              </div>
              <div className="flex flex-col gap-2">
                <SidebarLink
                  to="/admin"
                  label="Dashboard"
                  icon={<LayoutDashboard size={17} />}
                />
                <SidebarLink
                  to="/admin/tours"
                  label="Tours"
                  icon={<Trophy size={17} />}
                />
                <div className="my-3 h-px bg-black/5" />
                <SidebarLink
                  to="/more"
                  label="Back to Site"
                  icon={<Home size={17} />}
                />
              </div>
            </aside>
          </>
        )}

        <main className="min-w-0 flex-1 overflow-x-hidden bg-canvas px-3 py-4 pb-20 sm:px-4 md:px-8 md:py-8 md:pb-8">
          <div className="mx-auto w-full max-w-6xl min-w-0">
            <Outlet />
          </div>
        </main>
      </div>

      <nav className="fixed bottom-0 left-0 right-0 z-30 flex h-14 items-center justify-around border-t border-muted/30 bg-surface text-xs md:hidden">
        <BottomNavLink to="/admin" label="Dashboard" />
        <BottomNavLink to="/admin/tours" label="Tours" />
      </nav>
    </div>
  );
}

function SidebarLink({
  to,
  label,
  icon
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `
        flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors
        ${
          isActive
            ? "bg-primary/10 text-primary"
            : "text-mutedText hover:bg-black/[0.03] hover:text-text"
        }
      `
      }
    >
      <span className="shrink-0">{icon}</span>
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

function BottomNavLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `
          flex flex-col items-center gap-1
          ${isActive ? "text-primary" : "text-muted"}
        `
      }
    >
      <span>{label}</span>
    </NavLink>
  );
}

function getPageLabel(path: string) {
  if (path.includes("/matches/")) return "Match Builder";
  if (path.includes("/events/")) return "Event Builder";
  if (path.includes("/seasons/")) return "Season Builder";
  if (path.includes("/tours/")) return "Tour Builder";
  if (path.includes("tours")) return "Tours";
  return "Dashboard";
}
